/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useDictionary, useParameter } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { getDateParts } from "../utils/dateHelpers";
import { dictionaryService } from "../../../dataFlow";
import { SLIDE_NUMBER } from "../constants";

const CATEGORIES_AXIS = "commodities.default";
const SERIES_AXIS = "calendar.default";
const UNITS_AXIS = "units.default";
const [YEAR_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY] = [1, 4, 7];

export function useHighchartTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();

  const [commoditiesColumns] = useDictionary(dictionaryService, "v_dict_commodities_alternative_price", undefined, {
    attributes: "",
    slide: SLIDE_NUMBER,
  });
  const [unitsColumns] = useDictionary(dictionaryService, "v_dict_units_alternative_price", undefined, {
    attributes: "",
    slide: SLIDE_NUMBER,
  });
  const [selectedCommodityId] = useParameter<number>("@selected_commodity_id");

  const olapColumns = useMemo(() => {
    return [olapData].filter(Boolean).flatMap((e) => e?.columns);
  }, [olapData]);

  const olapMonthlyRows = useMemo(
    () =>
      olapData?.rows.filter((olapRow) => {
        const calendarId = olapRow.attributes[`${SERIES_AXIS}.id`];
        const { frequency } = getDateParts(calendarId);

        return Number(frequency) === MONTH_FREQUENCY;
      }),
    [olapData],
  );

  const commodityItem = useMemo(() => {
    let commodity;

    if (selectedCommodityId != null) {
      commodity = olapColumns.find((column) => {
        return column?.selection[CATEGORIES_AXIS] === Number(selectedCommodityId);
      });
    }

    let name = commodity?.headers.find((item) => item.id === `${CATEGORIES_AXIS}.name`)?.name;
    if (name == null) {
      const displayItem = commoditiesColumns?.find(
        (item) => item.getValue<number>("id") === Number(selectedCommodityId),
      );
      name = displayItem?.getValue<any>("name");
    }

    return {
      commodityId: commodity?.selection[CATEGORIES_AXIS] || selectedCommodityId,
      name: typeof name === "object" ? name[9] : name,
    };
  }, [selectedCommodityId, olapColumns, commoditiesColumns, unitsColumns]);

  const [monthesCategories] = useMemo(
    () =>
      [MONTH_FREQUENCY].map((frequency) => {
        const frequencyColumns = olapMonthlyRows?.filter((row) => {
          const colFrequency = +String(row.selection[SERIES_AXIS])[0];
          return colFrequency === frequency;
        });

        return frequencyColumns?.map((column) => {
          const name = column?.attributes[`${SERIES_AXIS}.name`];
          return {
            name,
          };
        });
      }) || [],
    [olapMonthlyRows],
  );

  const options = useChartOptions(
    {
      tooltip: {
        useHTML: true,
        formatter: function() {
          const { category, unit } = this.point as any;
          const pointValue = Highcharts.numberFormat(this.point.y || 0, 2);
          return `
            <div>
              <b>${this.point.series.name}</b><br/>
              <b>${category}</b>: <span>${pointValue} ${unit}</span>
            </div>
          `;
        },
        outside: true,
        style: {
          zIndex: 100,
        },
      },
    },
    [
      chartPipe("spline"),
      legendPipe("bottom", {
        useHTML: true,
        labelFormatter: function() {
          const { name } = this;
          return name;
        },
      }),
      addXAxisPipe(
        monthesCategories?.map((category) => category.name),
        {
          visible: true,
          labels: {
            enabled: true,
            formatter: function() {
              const label = this.axis.defaultLabelFormatter.call(this);
              return label;
            },
          },
        },
      ),
      addYAxisPipe({
        gridLineColor: "#fff",
        title: {
          text: "",
        },
        labels: {
          enabled: true,
          formatter: function() {
            const label = this.axis.defaultLabelFormatter.call(this);
            if (/^[0-9]{4}$/.test(label)) {
              return Highcharts.numberFormat(this.value, 0);
            }
            return label;
          },
        },
      }),
    ],
  );

  useEffect(() => {
    if (olapData) {
      const colors = ["#1CA955", "#E38114", "#DD5C3D", "#8F8F8F", "#D9D10C", "#2D5EDA", "#784FBA", "#D656A3"];

      const series: SeriesOptionsType[] = [
        {
          name: commodityItem.name,
          type: "spline",
          data: olapMonthlyRows?.map((row, rowIdx) => {
            const point = row.points.find(
              (p) => p?.attributes && Number(p.attributes[`${CATEGORIES_AXIS}.id`]) === commodityItem.commodityId,
            );
            const value = point?.value || null;
            const date = (point?.attributes && point?.attributes[`${SERIES_AXIS}.name`]) ?? "";
            const unit = (point?.attributes && point?.attributes[`${UNITS_AXIS}.name`][9]) ?? "";

            return {
              date,
              unit,
              x: rowIdx,
              y: value,
              isLast: rowIdx === olapMonthlyRows.length - 1,
            };
          }),
          yAxis: 0,
          xAxis: 0,
          color: colors[Math.floor(Math.random() * colors.length)],
          zIndex: 2,
          marker: {
            radius: 0,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
        },
      ];

      const result: Highcharts.Options = {
        ...options,
        series,
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapColumns, commodityItem]);

  return state;
}
